import { useState } from "react";
import Swal from "sweetalert2";
import {
  useModifierPartageMutation,
  usePartageQuery,
} from "../../../../utils/api/launchCampagn/projet.api";
import { Projet } from "../../../../utils/api/launchCampagn/projet.type";
import { COLORS } from "../../../../utils/constants";

const CHOIX = [
  {
    valeur: "public" as const,
    titre: "Public",
    aide: "Visible sur la plateforme : accueil, liste des campagnes, recherche.",
  },
  {
    valeur: "prive" as const,
    titre: "Privé",
    aide: "Invisible sur la plateforme. Seules les personnes qui ont le lien peuvent voir le projet et y contribuer.",
  },
];

// Le lien a partager. Un projet public deja valide a une adresse lisible ;
// sinon on donne le lien secret, qui marche aussi avant la validation (la
// page s'affiche, les contributions ouvriront ensuite).
function lienAPartager(slug: string, lien: string, visibilite: string, statut: string) {
  const origine = window.location.origin;
  if (visibilite === "public" && statut === "online")
    return `${origine}/app/campagnes/${slug}`;
  return `${origine}/app/c/${lien}`;
}

// Encadre de partage, affiche au porteur en haut de la page de son projet
// (bouton Voir). Les visiteurs ne le voient jamais : le serveur ne renvoie
// le lien qu'au porteur et a l'admin.
export function PartageProjetEncart({
  projet,
  onVisibilite,
}: {
  projet: Projet;
  onVisibilite?: (v: "public" | "prive") => void;
}) {
  const { data, isLoading, isError } = usePartageQuery(projet.id);
  const [modifier, { isLoading: enCours }] = useModifierPartageMutation();
  const [copie, setCopie] = useState(false);

  const changerVisibilite = async (visibilite: "public" | "prive") => {
    if (!data || data.visibilite === visibilite) return;
    // On dit ce que le changement implique avant de l'enregistrer : passer
    // en public met le projet en vitrine, passer en prive le retire.
    const valide = data.statut === "online";
    const choix = await Swal.fire({
      icon: "question",
      title: visibilite === "prive" ? "Passer le projet en privé ?" : "Rendre le projet public ?",
      text:
        visibilite === "prive"
          ? "Il n'apparaîtra plus sur la plateforme. Seules les personnes qui ont le lien pourront le voir et y contribuer."
          : valide
          ? "Il apparaîtra sur la plateforme : accueil, liste des campagnes, recherche."
          : "Il apparaîtra sur la plateforme dès que l'équipe Maajjal l'aura validé.",
      showCancelButton: true,
      confirmButtonText: "Enregistrer",
      cancelButtonText: "Annuler",
      confirmButtonColor: COLORS.blue,
    });
    if (!choix.isConfirmed) return;
    const res: any = await modifier({ id: projet.id, visibilite });
    if (res?.error) {
      return Swal.fire({
        icon: "error",
        title: "Échec",
        text: res.error?.data?.message || "Réessayez.",
        confirmButtonColor: COLORS.blue,
      });
    }
    onVisibilite?.(visibilite);
    Swal.fire({
      icon: "success",
      title: visibilite === "prive" ? "Projet passé en privé" : "Projet rendu public",
      iconColor: COLORS.green,
      showConfirmButton: false,
      timer: 1500,
    });
  };

  const lien = data
    ? lienAPartager(data.slug, data.lien_secret, data.visibilite, data.statut)
    : "";

  const copier = async () => {
    try {
      await navigator.clipboard.writeText(lien);
      setCopie(true);
      setTimeout(() => setCopie(false), 2000);
    } catch {
      // Presse-papiers refuse (page non securisee) : le lien reste selectionnable.
    }
  };

  const whatsapp = `https://wa.me/?text=${encodeURIComponent(
    `Soutenez « ${projet.name} » sur Maajjal : ${lien}`
  )}`;

  return (
    <div
      className="mb-4"
      style={{
        border: `1px solid ${COLORS.blue}`,
        borderRadius: 12,
        padding: 20,
        background: "#fff",
        textAlign: "left",
      }}
    >
      <div style={{ fontFamily: "Poppins", fontWeight: 600, color: COLORS.blue, fontSize: 16 }}>
        Partager votre projet
      </div>
      <div style={{ fontSize: 12, color: "rgba(0,0,0,0.5)", marginBottom: 12 }}>
        Visible par vous seul — les contributeurs voient la page ci-dessous.
      </div>

      {isLoading && <p className="mb-0">Chargement…</p>}
      {isError && (
        <div className="alert alert-danger mb-0">Impossible de charger le partage.</div>
      )}

      {data?.statut === "termine" && (
        <div className="alert alert-secondary mb-0" style={{ fontSize: 13 }}>
          Campagne terminée : les contributions sont fermées, et il n'y a plus
          rien à partager. Seul l'administrateur peut la rouvrir.
        </div>
      )}

      {data && data.statut !== "termine" && (
        <div className="row g-3">
          <div className="col-md-6">
            <div className="mb-2" style={{ fontSize: 13, fontWeight: 600 }}>Visibilité</div>
            <div className="d-flex flex-column gap-2">
              {CHOIX.map((c) => {
                const actif = data.visibilite === c.valeur;
                return (
                  <button
                    key={c.valeur}
                    type="button"
                    disabled={enCours}
                    onClick={() => changerVisibilite(c.valeur)}
                    style={{
                      textAlign: "left",
                      padding: "10px 14px",
                      borderRadius: 8,
                      background: actif ? "rgba(2,24,73,0.06)" : "#fff",
                      border: `1px solid ${actif ? COLORS.blue : "#d0d0d0"}`,
                    }}
                  >
                    <div style={{ fontWeight: 600, color: COLORS.blue, fontSize: 14 }}>
                      {actif ? "● " : "○ "}
                      {c.titre}
                    </div>
                    <div style={{ fontSize: 12, color: "rgba(0,0,0,0.6)" }}>{c.aide}</div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="col-md-6">
            <div className="mb-2" style={{ fontSize: 13, fontWeight: 600 }}>Lien à partager</div>
            <div className="d-flex gap-2">
              <input
                readOnly
                value={lien}
                onFocus={(e) => e.target.select()}
                className="form-control"
                style={{ fontSize: 13 }}
              />
              <button
                type="button"
                className="btn btn-sm"
                onClick={copier}
                style={{ background: COLORS.blue, color: "#fff", whiteSpace: "nowrap", padding: "0 14px" }}
              >
                {copie ? "Copié !" : "Copier"}
              </button>
            </div>
            <a
              href={whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-sm mt-2"
              style={{ background: COLORS.green, color: "#fff" }}
            >
              Partager sur WhatsApp
            </a>
            {data.statut !== "online" ? (
              <div className="alert alert-warning mt-3 mb-0" style={{ fontSize: 13 }}>
                Votre projet attend la validation de l'équipe Maajjal. Le lien
                ouvre déjà la page, mais les contributions ne seront possibles
                qu'une fois le projet validé.
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}
